import { axiosInstance } from '.'
import {
  UPDATE_KYC_DOCUMENT,
  ACCEPT_NDA_V1,
  GET_KYC_V1,
  GENERATE_SUMSUB_SDK_LINK,
  GENERATE_SUMSUB_ACCESS_TOKEN,
  ACCEPT_INVESTMENT_ESSENTIAL_AGREEMENT,
  ACCEPT_TOB,
  GET_INVESTOR_WITH_KYC,
  ACCEPT_TRANSACTION_CHECKLIST_AGREEMENT,
  SUBMIT_CLIENT_AGREEMENT,
  GET_INVESTOR_BANK_DETAILS,
  ACKNOWLEDGE_MARKED_DORMANT_MODAL,
  GET_INVESTOR_DORMANCY_REMINDER_DETAILS,
  ACCEPT_INVESTOR_DORMANCY_REMINDER,
  EXTEND_INVESTMENT_LIMIT,
  GET_MY_INVESTMENT_LIMIT_REQUEST,
  ONBOARDING_BANK_STATEMENTS,
  GET_MY_ONBOARDING_BANK_STATEMENTS,
  UPDATE_INVESTOR_BANK_DETAILS,
  GET_SIGNED_SELF_DECLARATION_DOCUMENT,
  CHECK_SELF_DECLARATION_REQUIRED,
  GET_INVESTOR_KYC_DOCUMENTS_CHECKLIST,
  GET_SUMSUB_DOCUMENT,
  GET_INVESTOR_PORTFOLIO_IDS,
} from './endpoints'
import { AcceptAgreement, AcceptDormancyReminder } from '../lib/types'
import { ExtendLimitPayload, KYCService, OnboardingBankStatementsPayload } from './apiTypes_v1'

export const kycServices: KYCService = {
  updateKYCDocument: async (id: any, updateData: any, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(UPDATE_KYC_DOCUMENT(id), updateData, {
        headers: headers,
      })
    } catch (error) {
      return error
    }
  },

  // V1
  acceptTNCAndNDA_v1: async (selfDeclarationData, headers) => {
    try {
      const res = await axiosInstance.post(ACCEPT_NDA_V1, selfDeclarationData, {
        headers: headers,
      })
      return res
    } catch (error: any) {
      return error
    }
  },

  acceptInvestmentEssentialAgreement: async (
    declarationData: AcceptAgreement,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ACCEPT_INVESTMENT_ESSENTIAL_AGREEMENT, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  acceptTermsOfBusinessAgreement: async (
    declarationData: AcceptAgreement,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ACCEPT_TOB, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  acceptTransactionChecklistAgreement: async (
    declarationData: AcceptAgreement,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ACCEPT_TRANSACTION_CHECKLIST_AGREEMENT, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  submitClientAgreement: async (
    declarationData: AcceptAgreement,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(SUBMIT_CLIENT_AGREEMENT, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  acceptDormancyAcknowledgment: async (
    declarationData: AcceptAgreement,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ACKNOWLEDGE_MARKED_DORMANT_MODAL, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  getKYC_v1: async (id: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_KYC_V1(id), {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  getInvestorWithKyc: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_INVESTOR_WITH_KYC, { headers: headers })
    } catch (error: any) {
      return error.response
    }
  },

  generateSumsubSDKLink: async (
    headers: { [key: string]: string },
    entityName: string,
    ttlInSecs?: number,
  ) => {
    try {
      const payload = {
        entityName,
        ttlInSecs,
      }
      return await axiosInstance.post(GENERATE_SUMSUB_SDK_LINK, payload, {
        headers: headers,
      })
    } catch (error: any) {
      return Promise.reject(error)
    }
  },

  generateSumsubAccessToken: async (headers: { [key: string]: string }, entityName: string) => {
    try {
      return await axiosInstance.post(
        GENERATE_SUMSUB_ACCESS_TOKEN,
        { entityName },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return Promise.reject(error)
    }
  },

  getInvestorBankDetails: async (userId: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_INVESTOR_BANK_DETAILS(userId), {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  updateBankDetails: async (data: any, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.post(UPDATE_INVESTOR_BANK_DETAILS, data, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getInvestorDormancyReminderDetails: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_INVESTOR_DORMANCY_REMINDER_DETAILS, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },
  acceptInvestorDormancyReminder: async (
    declarationData: AcceptDormancyReminder,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.post(ACCEPT_INVESTOR_DORMANCY_REMINDER, declarationData, {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  extendLimit: async (payload: ExtendLimitPayload, headers: { [key: string]: string }) => {
    return await axiosInstance.patch(EXTEND_INVESTMENT_LIMIT, payload, { headers })
  },

  getMyInvestmentLimitRequest: async (headers: { [key: string]: string }) => {
    return await axiosInstance.get(GET_MY_INVESTMENT_LIMIT_REQUEST, { headers })
  },

  submitOnboardingBankStatements: async (
    payload: OnboardingBankStatementsPayload,
    headers: { [key: string]: string },
  ) => {
    return await axiosInstance.post(ONBOARDING_BANK_STATEMENTS, payload, { headers })
  },

  getMyOnboardingBankStatements: async (headers: { [key: string]: string }) => {
    return await axiosInstance.get(GET_MY_ONBOARDING_BANK_STATEMENTS, { headers })
  },

  getSignedSelfDeclarationDocument: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_SIGNED_SELF_DECLARATION_DOCUMENT, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  checkSelfDeclarationRequired: async (headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(CHECK_SELF_DECLARATION_REQUIRED, {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getInvestorKYCDocumentsChecklist: async (
    investorId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(GET_INVESTOR_KYC_DOCUMENTS_CHECKLIST(investorId), {
        headers: headers,
      })
    } catch (error: any) {
      return error
    }
  },

  getSumsubDocument: async (
    investorId: string,
    documentId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(GET_SUMSUB_DOCUMENT(investorId, documentId), {
        headers: headers,
        responseType: 'blob',
      })
    } catch (error: any) {
      return error.response
    }
  },

  getInvestorPortfolioIds: async (investorId: string, headers: { [key: string]: string }) => {
    try {
      return await axiosInstance.get(GET_INVESTOR_PORTFOLIO_IDS(investorId), {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },
}
